import { StatusBar } from "expo-status-bar";
import {
  Alert,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  useColorScheme,
} from "react-native";
import { useEffect, useMemo, useRef, useState } from "react";
import { MaterialIcons } from "@expo/vector-icons";
import { Picker } from "@react-native-picker/picker";
import { View } from "../components/Themed";
import {
  APP_THEME_PRESETS,
  MAX_UPLOADED_FONTS,
  MAX_WEB_FONT_BYTES,
  createUploadedWebFontEntry,
  getCurrentAppTheme,
  getDefaultAppTheme,
  getThemeFontOptions,
  loadAppTheme,
  registerThemeUploadedFonts,
  removeUploadedFont,
  resetAppTheme,
  saveAppTheme,
} from "../utils/themeDesigner";

const FIELD_DEFS = [
  { key: "primary", label: "Primary" },
  { key: "secondary", label: "Secondary" },
  { key: "accent", label: "Accent" },
  { key: "backgroundLight", label: "Background (Light)" },
  { key: "surfaceLight", label: "Surface (Light)" },
  { key: "textLight", label: "Text (Light)" },
  { key: "backgroundDark", label: "Background (Dark)" },
  { key: "surfaceDark", label: "Surface (Dark)" },
  { key: "textDark", label: "Text (Dark)" },
];

const FONT_ROLE_DEFS = [
  { key: "logoFontFamily", label: "Logo Font" },
  { key: "titleFontFamily", label: "Title Font" },
  { key: "bodyFontFamily", label: "Body Font" },
];

const QUICK_SWATCHES = [
  "#d62828",
  "#2e7d32",
  "#1565c0",
  "#f59e0b",
  "#7c3aed",
  "#e11d48",
  "#0284c7",
  "#10b981",
  "#111827",
  "#f8fafc",
  "#ffffff",
  "#000000",
];

const FONT_FILE_ACCEPT = ".ttf,.otf,.woff,.woff2,font/ttf,font/otf,font/woff,font/woff2,application/font-sfnt";

function normalizeHexInput(value) {
  const cleaned = String(value || "")
    .toLowerCase()
    .replace(/[^#0-9a-f]/g, "");
  if (!cleaned) {
    return "";
  }
  const prefixed = cleaned.startsWith("#") ? cleaned : `#${cleaned}`;
  return prefixed.slice(0, 7);
}

function toSafeHex(value, fallback) {
  const text = String(value || "").trim().toLowerCase();
  if (/^#[0-9a-f]{6}$/.test(text)) {
    return text;
  }
  if (/^#[0-9a-f]{3}$/.test(text)) {
    return `#${text[1]}${text[1]}${text[2]}${text[2]}${text[3]}${text[3]}`;
  }
  return fallback;
}

function toPrettyBytes(value) {
  const bytes = Number(value);
  if (!Number.isFinite(bytes) || bytes <= 0) {
    return "";
  }
  if (bytes >= 1024 * 1024) {
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  }
  if (bytes >= 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`;
  }
  return `${bytes} B`;
}

function isFontLikeFile(file) {
  const mime = String(file?.type || "").toLowerCase();
  const name = String(file?.name || "").toLowerCase();
  if (mime.startsWith("font/") || mime === "application/font-sfnt") {
    return true;
  }
  return /\.(ttf|otf|woff|woff2)$/.test(name);
}

export default function OperatorSettingsThemeDesignerScreen() {
  const colorScheme = useColorScheme();
  const defaultTheme = useMemo(() => getDefaultAppTheme(), []);
  const [theme, setTheme] = useState(() => getCurrentAppTheme() || defaultTheme);
  const [isSaving, setIsSaving] = useState(false);
  const [uploadingFont, setUploadingFont] = useState(false);
  const webFontInputRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const stored = await loadAppTheme();
      if (!cancelled) {
        const next = stored || getCurrentAppTheme() || defaultTheme;
        setTheme(next);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [defaultTheme]);

  useEffect(() => {
    registerThemeUploadedFonts(theme);
  }, [theme]);

  const styles = useMemo(() => createStyles(colorScheme), [colorScheme]);
  const isDark = colorScheme === "dark";
  const previewBackground = isDark
    ? toSafeHex(theme.backgroundDark, defaultTheme.backgroundDark)
    : toSafeHex(theme.backgroundLight, defaultTheme.backgroundLight);
  const previewSurface = isDark
    ? toSafeHex(theme.surfaceDark, defaultTheme.surfaceDark)
    : toSafeHex(theme.surfaceLight, defaultTheme.surfaceLight);
  const previewText = isDark
    ? toSafeHex(theme.textDark, defaultTheme.textDark)
    : toSafeHex(theme.textLight, defaultTheme.textLight);
  const previewPrimary = toSafeHex(theme.primary, defaultTheme.primary);
  const previewSecondary = toSafeHex(theme.secondary, defaultTheme.secondary);
  const previewAccent = toSafeHex(theme.accent, defaultTheme.accent);
  const fontOptions = useMemo(() => getThemeFontOptions(theme), [theme]);
  const uploadedFonts = useMemo(
    () => (Array.isArray(theme.uploadedFonts) ? theme.uploadedFonts : []),
    [theme.uploadedFonts]
  );

  const updateColor = (key, value) => {
    setTheme((previous) => ({
      ...previous,
      [key]: normalizeHexInput(value),
    }));
  };

  const setSwatchColor = (key, value) => {
    setTheme((previous) => ({
      ...previous,
      [key]: value,
    }));
  };

  const updateFontRole = (key, value) => {
    setTheme((previous) => ({
      ...previous,
      [key]: String(value || "").trim(),
    }));
  };

  const applyPreset = (presetTheme) => {
    setTheme((previous) => ({
      ...previous,
      ...presetTheme,
    }));
  };

  const openFontPicker = () => {
    if (Platform.OS !== "web") {
      Alert.alert(
        "Upload Unsupported",
        "Direct font upload is available on web builds. On native, choose from installed fonts."
      );
      return;
    }

    webFontInputRef.current?.click?.();
  };

  const onWebFontSelected = (event) => {
    const file = event?.target?.files?.[0];
    if (!file) {
      return;
    }

    event.target.value = "";

    if (!isFontLikeFile(file)) {
      Alert.alert("Invalid File", "Choose a .ttf, .otf, .woff, or .woff2 font file.");
      return;
    }

    if (file.size > MAX_WEB_FONT_BYTES) {
      Alert.alert(
        "File Too Large",
        `Font file must be under ${toPrettyBytes(MAX_WEB_FONT_BYTES)}.`
      );
      return;
    }

    const reader = new FileReader();
    setUploadingFont(true);

    reader.onerror = () => {
      setUploadingFont(false);
      Alert.alert("Upload Failed", "Could not read font file.");
    };

    reader.onload = () => {
      try {
        const dataUrl = String(reader.result || "");
        if (!dataUrl.startsWith("data:")) {
          throw new Error("Invalid data URL.");
        }

        setTheme((previous) => {
          const existing = Array.isArray(previous.uploadedFonts) ? previous.uploadedFonts : [];
          if (existing.length >= MAX_UPLOADED_FONTS) {
            Alert.alert(
              "Font Limit Reached",
              `Keep up to ${MAX_UPLOADED_FONTS} uploaded fonts. Remove one to add another.`
            );
            return previous;
          }

          const entry = createUploadedWebFontEntry(file.name, dataUrl, file.type, file.size);
          return {
            ...previous,
            uploadedFonts: [...existing, entry],
            bodyFontFamily: entry.family,
          };
        });
      } catch (error) {
        Alert.alert("Upload Failed", error?.message || "Could not process font file.");
      } finally {
        setUploadingFont(false);
      }
    };

    reader.readAsDataURL(file);
  };

  const onRemoveUploadedFont = (fontId) => {
    setTheme((previous) => removeUploadedFont(previous, fontId));
  };

  const onSave = async () => {
    try {
      setIsSaving(true);
      const next = await saveAppTheme(theme);
      setTheme(next);
      Alert.alert("Theme Saved", "Theme colors and fonts have been updated.");
    } catch (error) {
      Alert.alert("Save Failed", error?.message || "Unable to save theme.");
    } finally {
      setIsSaving(false);
    }
  };

  const onReset = () => {
    Alert.alert(
      "Reset Theme",
      "Reset colors and typography to defaults?",
      [
        { text: "Cancel", style: "cancel" },
        {
          text: "Reset",
          style: "destructive",
          onPress: async () => {
            try {
              await resetAppTheme();
              const next = getCurrentAppTheme() || getDefaultAppTheme();
              setTheme(next);
            } catch (error) {
              Alert.alert("Reset Failed", error?.message || "Unable to reset theme.");
            }
          },
        },
      ]
    );
  };

  return (
    <View style={styles.page}>
      <ScrollView contentContainerStyle={styles.content} showsVerticalScrollIndicator={false}>
        <Text style={styles.title}>Theme Designer</Text>
        <Text style={styles.subtitle}>
          Customize app palette and typography, then save.
        </Text>

        <View style={[styles.previewWrap, { backgroundColor: previewBackground, borderColor: previewAccent }]}>
          <Text
            style={[
              styles.previewLogo,
              {
                color: previewPrimary,
                fontFamily: theme.logoFontFamily || defaultTheme.logoFontFamily,
              },
            ]}
          >
            Venue
          </Text>
          <Text
            style={[
              styles.previewHeading,
              {
                color: previewText,
                fontFamily: theme.titleFontFamily || defaultTheme.titleFontFamily,
              },
            ]}
          >
            Live Preview
          </Text>
          <View style={[styles.previewCard, { backgroundColor: previewSurface }]}>
            <Text
              style={[
                styles.previewTitle,
                {
                  color: previewText,
                  fontFamily: theme.titleFontFamily || defaultTheme.titleFontFamily,
                },
              ]}
            >
              Venue Operator App
            </Text>
            <Text
              style={[
                styles.previewBody,
                {
                  color: previewText,
                  fontFamily: theme.bodyFontFamily || defaultTheme.bodyFontFamily,
                },
              ]}
            >
              This panel reflects your selected palette and typography.
            </Text>
            <View style={styles.previewChipRow}>
              <View style={[styles.previewChip, { backgroundColor: previewPrimary }]}>
                <Text style={[styles.previewChipText, { fontFamily: theme.bodyFontFamily || defaultTheme.bodyFontFamily }]}>
                  Primary
                </Text>
              </View>
              <View style={[styles.previewChip, { backgroundColor: previewSecondary }]}>
                <Text style={[styles.previewChipText, { fontFamily: theme.bodyFontFamily || defaultTheme.bodyFontFamily }]}>
                  Secondary
                </Text>
              </View>
              <View style={[styles.previewChip, { backgroundColor: previewAccent }]}>
                <Text style={[styles.previewChipText, { fontFamily: theme.bodyFontFamily || defaultTheme.bodyFontFamily }]}>
                  Accent
                </Text>
              </View>
            </View>
            <Pressable style={[styles.previewButton, { backgroundColor: previewPrimary }]}>
              <Text style={[styles.previewButtonText, { fontFamily: theme.bodyFontFamily || defaultTheme.bodyFontFamily }]}>
                Sample Action
              </Text>
            </Pressable>
          </View>
        </View>

        <Text style={styles.sectionTitle}>Preset Palettes</Text>
        <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.presetRow}>
          {APP_THEME_PRESETS.map((preset) => (
            <Pressable
              key={preset.id}
              style={[styles.presetButton, { borderColor: toSafeHex(preset.theme.primary, defaultTheme.primary) }]}
              onPress={() => applyPreset(preset.theme)}
            >
              <View style={styles.presetSwatches}>
                <View style={[styles.presetSwatch, { backgroundColor: preset.theme.primary }]} />
                <View style={[styles.presetSwatch, { backgroundColor: preset.theme.secondary }]} />
                <View style={[styles.presetSwatch, { backgroundColor: preset.theme.accent }]} />
              </View>
              <Text style={styles.presetLabel}>{preset.name}</Text>
            </Pressable>
          ))}
        </ScrollView>

        <Text style={styles.sectionTitle}>Typography</Text>
        {FONT_ROLE_DEFS.map((role) => (
          <View key={role.key} style={styles.fieldCard}>
            <Text style={styles.fieldLabel}>{role.label}</Text>
            <View style={styles.pickerWrap}>
              <Picker
                selectedValue={theme[role.key] || defaultTheme[role.key]}
                onValueChange={(value) => updateFontRole(role.key, value)}
                style={styles.picker}
              >
                {fontOptions.map((option) => (
                  <Picker.Item key={`${role.key}-${option.value}`} label={option.label} value={option.value} />
                ))}
              </Picker>
            </View>
          </View>
        ))}

        <View style={styles.fieldCard}>
          <Text style={styles.fieldLabel}>Uploaded Fonts</Text>
          {Platform.OS === "web" ? (
            <>
              {/* eslint-disable-next-line react/no-unknown-property */}
              <input
                ref={webFontInputRef}
                type="file"
                accept={FONT_FILE_ACCEPT}
                style={{ display: "none" }}
                onChange={onWebFontSelected}
              />
              <Pressable
                style={[styles.uploadButton, uploadingFont && styles.uploadButtonDisabled]}
                onPress={openFontPicker}
                disabled={uploadingFont}
              >
                <MaterialIcons name="upload-file" size={18} color="#ffffff" />
                <Text style={styles.uploadButtonText}>{uploadingFont ? "Uploading..." : "Upload Font"}</Text>
              </Pressable>
              <Text style={styles.uploadHint}>
                Upload .ttf/.otf/.woff/.woff2 (max {toPrettyBytes(MAX_WEB_FONT_BYTES)}).
              </Text>
            </>
          ) : (
            <Text style={styles.uploadHint}>
              Font upload is available on web builds. Native can still use selected installed fonts.
            </Text>
          )}

          {uploadedFonts.length === 0 ? (
            <Text style={styles.emptyUploadedFonts}>No uploaded fonts yet.</Text>
          ) : (
            uploadedFonts.map((font) => (
              <View key={font.id} style={styles.uploadedFontRow}>
                <View style={styles.uploadedFontMeta}>
                  <Text style={[styles.uploadedFontName, { fontFamily: font.family }]}>{font.name}</Text>
                  <Text style={styles.uploadedFontInfo}>
                    {font.family}
                    {font.size ? ` • ${toPrettyBytes(font.size)}` : ""}
                  </Text>
                </View>
                <Pressable style={styles.removeUploadButton} onPress={() => onRemoveUploadedFont(font.id)}>
                  <MaterialIcons name="delete-outline" size={18} color="#dc2626" />
                  <Text style={styles.removeUploadText}>Remove</Text>
                </Pressable>
              </View>
            ))
          )}
        </View>

        <Text style={styles.sectionTitle}>Color Tokens</Text>
        {FIELD_DEFS.map((field) => {
          const value = String(theme[field.key] || "");
          const preview = toSafeHex(value, defaultTheme[field.key]);
          return (
            <View key={field.key} style={styles.fieldCard}>
              <View style={styles.fieldHeader}>
                <Text style={styles.fieldLabel}>{field.label}</Text>
                <View style={[styles.fieldPreview, { backgroundColor: preview }]} />
              </View>
              <TextInput
                value={value}
                onChangeText={(text) => updateColor(field.key, text)}
                placeholder="#000000"
                autoCapitalize="none"
                autoCorrect={false}
                style={styles.input}
              />
              <View style={styles.swatchRow}>
                {QUICK_SWATCHES.map((swatch) => (
                  <Pressable
                    key={`${field.key}-${swatch}`}
                    onPress={() => setSwatchColor(field.key, swatch)}
                    style={[styles.swatchButton, { backgroundColor: swatch }]}
                  />
                ))}
              </View>
            </View>
          );
        })}

        <View style={styles.actions}>
          <Pressable style={[styles.actionButton, styles.resetButton]} onPress={onReset}>
            <MaterialIcons name="restart-alt" size={18} color="#991b1b" />
            <Text style={styles.resetText}>Reset</Text>
          </Pressable>
          <Pressable
            style={[styles.actionButton, styles.saveButton, isSaving && styles.saveButtonDisabled]}
            onPress={() => {
              void onSave();
            }}
            disabled={isSaving}
          >
            <MaterialIcons name="save" size={18} color="#ffffff" />
            <Text style={styles.saveText}>{isSaving ? "Saving..." : "Save Theme"}</Text>
          </Pressable>
        </View>
      </ScrollView>
      <StatusBar style={Platform.OS === "ios" ? "light" : "auto"} />
    </View>
  );
}

function createStyles(colorScheme) {
  const isDark = colorScheme === "dark";
  return StyleSheet.create({
    page: {
      flex: 1,
    },
    content: {
      padding: 16,
      paddingBottom: 40,
    },
    title: {
      fontSize: 28,
      fontWeight: "800",
      color: isDark ? "#f8fafc" : "#0f172a",
      marginBottom: 6,
    },
    subtitle: {
      fontSize: 14,
      lineHeight: 20,
      color: isDark ? "#cbd5e1" : "#475569",
      marginBottom: 16,
    },
    previewWrap: {
      borderRadius: 16,
      borderWidth: 2,
      padding: 14,
      marginBottom: 16,
    },
    previewLogo: {
      fontSize: 34,
      letterSpacing: 0.3,
      marginBottom: 2,
    },
    previewHeading: {
      fontSize: 13,
      fontWeight: "700",
      marginBottom: 8,
      letterSpacing: 0.2,
    },
    previewCard: {
      borderRadius: 14,
      padding: 14,
    },
    previewTitle: {
      fontSize: 20,
      fontWeight: "800",
      marginBottom: 6,
    },
    previewBody: {
      fontSize: 13,
      lineHeight: 18,
      marginBottom: 10,
    },
    previewChipRow: {
      flexDirection: "row",
      gap: 8,
      marginBottom: 10,
      backgroundColor: "transparent",
    },
    previewChip: {
      borderRadius: 999,
      paddingHorizontal: 10,
      paddingVertical: 5,
    },
    previewChipText: {
      color: "#ffffff",
      fontSize: 11,
      fontWeight: "700",
    },
    previewButton: {
      borderRadius: 10,
      paddingVertical: 11,
      alignItems: "center",
      justifyContent: "center",
    },
    previewButtonText: {
      color: "#ffffff",
      fontSize: 14,
      fontWeight: "700",
    },
    sectionTitle: {
      fontSize: 18,
      fontWeight: "800",
      color: isDark ? "#f8fafc" : "#0f172a",
      marginBottom: 10,
      marginTop: 4,
    },
    presetRow: {
      paddingBottom: 8,
      gap: 8,
    },
    presetButton: {
      borderWidth: 2,
      borderRadius: 12,
      paddingHorizontal: 10,
      paddingVertical: 8,
      minWidth: 140,
      backgroundColor: isDark ? "#0f172a" : "#ffffff",
    },
    presetSwatches: {
      flexDirection: "row",
      gap: 6,
      marginBottom: 8,
      backgroundColor: "transparent",
    },
    presetSwatch: {
      width: 22,
      height: 22,
      borderRadius: 999,
    },
    presetLabel: {
      color: isDark ? "#e2e8f0" : "#1e293b",
      fontSize: 13,
      fontWeight: "700",
    },
    fieldCard: {
      borderRadius: 12,
      borderWidth: 1,
      borderColor: isDark ? "#334155" : "#cbd5e1",
      backgroundColor: isDark ? "#0f172a" : "#ffffff",
      padding: 10,
      marginBottom: 10,
    },
    fieldHeader: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      marginBottom: 8,
      backgroundColor: "transparent",
    },
    fieldLabel: {
      fontSize: 13,
      fontWeight: "700",
      color: isDark ? "#e2e8f0" : "#0f172a",
      marginBottom: 8,
    },
    fieldPreview: {
      width: 28,
      height: 28,
      borderRadius: 999,
      borderWidth: 1,
      borderColor: isDark ? "#334155" : "#94a3b8",
    },
    input: {
      borderRadius: 9,
      borderWidth: 1,
      borderColor: isDark ? "#334155" : "#cbd5e1",
      backgroundColor: isDark ? "#020617" : "#f8fafc",
      color: isDark ? "#f8fafc" : "#0f172a",
      paddingHorizontal: 10,
      paddingVertical: 8,
      fontSize: 13,
      marginBottom: 8,
    },
    swatchRow: {
      flexDirection: "row",
      flexWrap: "wrap",
      gap: 6,
      backgroundColor: "transparent",
    },
    swatchButton: {
      width: 22,
      height: 22,
      borderRadius: 999,
      borderWidth: 1,
      borderColor: isDark ? "#475569" : "#94a3b8",
    },
    pickerWrap: {
      borderRadius: 10,
      borderWidth: 1,
      borderColor: isDark ? "#334155" : "#cbd5e1",
      backgroundColor: isDark ? "#020617" : "#f8fafc",
      overflow: "hidden",
    },
    picker: {
      color: isDark ? "#f8fafc" : "#0f172a",
    },
    uploadButton: {
      marginTop: 4,
      borderRadius: 10,
      backgroundColor: "#1d4ed8",
      alignItems: "center",
      justifyContent: "center",
      flexDirection: "row",
      gap: 8,
      paddingVertical: 10,
    },
    uploadButtonDisabled: {
      opacity: 0.7,
    },
    uploadButtonText: {
      color: "#ffffff",
      fontSize: 14,
      fontWeight: "700",
    },
    uploadHint: {
      marginTop: 8,
      color: isDark ? "#94a3b8" : "#475569",
      fontSize: 12,
      lineHeight: 17,
    },
    emptyUploadedFonts: {
      marginTop: 10,
      color: isDark ? "#94a3b8" : "#64748b",
      fontSize: 12,
    },
    uploadedFontRow: {
      marginTop: 10,
      borderRadius: 10,
      borderWidth: 1,
      borderColor: isDark ? "#334155" : "#dbeafe",
      backgroundColor: isDark ? "#111827" : "#f8fafc",
      padding: 10,
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
    },
    uploadedFontMeta: {
      flex: 1,
      backgroundColor: "transparent",
      marginRight: 8,
    },
    uploadedFontName: {
      fontSize: 14,
      color: isDark ? "#f8fafc" : "#0f172a",
      marginBottom: 2,
    },
    uploadedFontInfo: {
      fontSize: 11,
      color: isDark ? "#93c5fd" : "#1d4ed8",
    },
    removeUploadButton: {
      borderRadius: 8,
      paddingVertical: 6,
      paddingHorizontal: 8,
      backgroundColor: "#fee2e2",
      flexDirection: "row",
      gap: 5,
      alignItems: "center",
    },
    removeUploadText: {
      color: "#b91c1c",
      fontSize: 12,
      fontWeight: "700",
    },
    actions: {
      flexDirection: "row",
      gap: 10,
      marginTop: 6,
      backgroundColor: "transparent",
    },
    actionButton: {
      flex: 1,
      borderRadius: 10,
      flexDirection: "row",
      justifyContent: "center",
      alignItems: "center",
      paddingVertical: 11,
      gap: 6,
    },
    saveButton: {
      backgroundColor: "#1d4ed8",
    },
    saveButtonDisabled: {
      opacity: 0.7,
    },
    saveText: {
      color: "#ffffff",
      fontSize: 14,
      fontWeight: "700",
    },
    resetButton: {
      backgroundColor: "#fee2e2",
      borderWidth: 1,
      borderColor: "#fecaca",
    },
    resetText: {
      color: "#991b1b",
      fontSize: 14,
      fontWeight: "700",
    },
  });
}

